import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { ToastService, ToastPosition } from './toast.service';
import { ToastComponent } from './toast.component';

/** F-FU-45 · class that anchors a top toast stack under the app header. */
export const TOAST_STACK_BELOW_HEADER_CLASS = 'ui-toast-stack--below-header';

/**
 * GAP-FE-021B · migrated from decorator-based @Input to signal-based input().
 */
@Component({
  selector: 'ui-toast-container',
  standalone: true,
  imports: [ToastComponent],
  templateUrl: './toast-container.component.html',
  styleUrl: './toast-container.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToastContainerComponent {
  readonly position = input<ToastPosition>('top-right');

  toastService = inject(ToastService);

  readonly containerClasses = computed<string>(() => {
    const baseClasses = [
      'fixed',
      'z-notification',
      'flex',
      'flex-col',
      'gap-2',
      'p-4',
      'pointer-events-none',
      '[&>*]:pointer-events-auto'
    ];

    // RF-QA-184: top-anchored toasts start BELOW the app header so they never
    // cover the clock / notifications / profile controls; the z-notification
    // layer is kept so modals cannot hide a toast.
    // F-FU-45: the header is not always at y=0 — the update / mock-data banners
    // render above it — so `ui-toast-stack--below-header` (component css) uses
    // `top: var(--rz-header-bottom, 3rem)`, the navbar's real bottom edge
    // published by the shell's PublishHeaderBottomDirective (3rem = h-12 navbar
    // when nothing publishes, e.g. on /auth pages).
    const positionClasses: Record<ToastPosition, string[]> = {
      'top-right': [TOAST_STACK_BELOW_HEADER_CLASS, 'right-0', 'items-end'],
      'top-left': [TOAST_STACK_BELOW_HEADER_CLASS, 'left-0', 'items-start'],
      'bottom-right': ['bottom-0', 'right-0', 'items-end'],
      'bottom-left': ['bottom-0', 'left-0', 'items-start'],
      'top-center': [TOAST_STACK_BELOW_HEADER_CLASS, 'left-1/2', '-translate-x-1/2', 'items-center'],
      'bottom-center': ['bottom-0', 'left-1/2', '-translate-x-1/2', 'items-center']
    };

    return [...baseClasses, ...positionClasses[this.position()]].join(' ');
  });

  trackById(_: number, toast: { id: string }): string {
    return toast.id;
  }
}
